import React, { useState, useCallback, useEffect, useRef } from "react";
import styled from "styled-components";
import { io, Socket } from "socket.io-client";
import axios from "axios";
import { useInterval } from "../hooks/useInterval";
import { useAuth } from "../contexts/AuthContext";

const Container = styled.div`
  max-width: 1800px;
  margin: 0 auto;
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  border: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  color: #1f2937;
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 8px;
`;

const Description = styled.p`
  color: #6b7280;
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 32px;
`;

const StatusSection = styled.div`
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
`;

const StatusLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
`;

const StatusIndicator = styled.div<{ $isRunning: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 1rem;
  font-weight: 500;
  color: ${(props) => (props.$isRunning ? "#059669" : "#6b7280")};
`;

const StatusDot = styled.div<{ $isRunning: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${(props) => (props.$isRunning ? "#10b981" : "#9ca3af")};
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const Button = styled.button<{
  $variant?: "primary" | "secondary";
  $disabled?: boolean;
}>`
  background: ${(props) => {
    if (props.$disabled) return "#f3f4f6";
    return props.$variant === "secondary" ? "#ef4444" : "#667eea";
  }};
  color: ${(props) => (props.$disabled ? "#9ca3af" : "white")};
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${(props) => {
    if (props.$disabled) return "#f3f4f6";
    return props.$variant === "secondary" ? "#dc2626" : "#5a67d8";
  }};
    transform: ${(props) => (props.$disabled ? "none" : "translateY(-1px)")};
  }

  &:active {
    transform: ${(props) => (props.$disabled ? "none" : "translateY(0)")};
  }
`;

const Icon = styled.span`
  font-size: 1.2rem;
`;

const LoadingSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const MessageContainer = styled.div<{ $type: "success" | "error" | "info" }>`
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.$type) {
      case "success":
        return "#d1fae5";
      case "error":
        return "#fee2e2";
      case "info":
        return "#dbeafe";
      default:
        return "#f3f4f6";
    }
  }};
  color: ${(props) => {
    switch (props.$type) {
      case "success":
        return "#065f46";
      case "error":
        return "#991b1b";
      case "info":
        return "#1e40af";
      default:
        return "#374151";
    }
  }};
  border: 1px solid
    ${(props) => {
    switch (props.$type) {
      case "success":
        return "#a7f3d0";
      case "error":
        return "#fecaca";
      case "info":
        return "#93c5fd";
      default:
        return "#e5e7eb";
    }
  }};
`;

const ContactDataCard = styled.div`
  background: #f0f9ff;
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
`;

const ContactDataTitle = styled.h3`
  color: #0c4a6e;
  font-size: 1.125rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ContactDataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 16px;
`;

const ContactDataItem = styled.div`
  background: white;
  border: 1px solid #e0f2fe;
  border-radius: 8px;
  padding: 12px;
`;

const ContactDataLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: #0369a1;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
`;

const ContactDataValue = styled.div`
  font-size: 0.875rem;
  color: #1e293b;
  font-weight: 500;
  word-break: break-word;
`;

const ViewerSection = styled.div`
  margin-top: 32px;
`;

const ViewerTitle = styled.h3`
  color: #1f2937;
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ViewerContainer = styled.div`
  background: #f8fafc;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 16px;
  min-height: 600px;
  position: relative;
  cursor: default !important;
`;

const ViewerIframe = styled.iframe`
  width: 100%;
  height: 1000px;
  border: none;
  border-radius: 8px;
  background: white;
`;

const ViewerPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 1000px;
  color: #6b7280;
  font-size: 1rem;
  text-align: center;
`;

const ViewerStatus = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoadingCard = styled.div`
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 120px;
`;

const LoadingAnimation = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const ModalTitle = styled.h3`
  color: #1f2937;
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalDescription = styled.p`
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 12px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  resize: vertical;
  margin-bottom: 16px;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button<{
  $variant?: "primary" | "secondary";
  $disabled?: boolean;
}>`
  background: ${(props) => {
    if (props.$disabled) return "#f3f4f6";
    return props.$variant === "secondary" ? "#6b7280" : "#667eea";
  }};
  color: ${(props) => (props.$disabled ? "#9ca3af" : "white")};
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${(props) => {
    if (props.$disabled) return "#f3f4f6";
    return props.$variant === "secondary" ? "#4b5563" : "#5a67d8";
  }};
  }
`;

const InstructionsList = styled.ol`
  color: #374151;
  font-size: 0.875rem;
  line-height: 1.6;
  margin-bottom: 20px;
  padding-left: 20px;

  li {
    margin-bottom: 8px;
  }

  code {
    background: #f3f4f6;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 0.8rem;
  }
`;

interface ContactData {
  first_name: string;
  last_name: string;
  company?: string;
  role?: string;
  email?: string;
  phone?: string;
  linkedin_url?: string;
  sales_navigator_url?: string;
}

const ScraperControl: React.FC = () => {
  const { hasRole } = useAuth();
  const isAdmin = hasRole(['admin']);
  
  const [isRunning, setIsRunning] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{
    text: string;
    type: "success" | "error" | "info";
  } | null>(null);
  const [showCookieModal, setShowCookieModal] = useState(false);
  const [cookieInput, setCookieInput] = useState("");
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [currentPage, setCurrentPage] = useState<{
    url: string;
    html: string;
    contactId?: string;
    timestamp: string;
  } | null>(null);
  const [currentContactData, setCurrentContactData] =
    useState<ContactData | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const currentContactIdRef = useRef<string | null>(null);

  // Poll scraper status every 5 seconds
  const fetchStatus = useCallback(async () => {
    try {
      const response = await axios.get("/api/scraper/status");

      if (response.data.success && response.data.data) {
        const wasRunning = isRunning;
        const nowRunning = response.data.data.running;

        setIsRunning(nowRunning);

        // Log status changes
        if (wasRunning !== nowRunning) {
          console.log(
            `📊 Scraper status changed: ${wasRunning ? "running" : "stopped"
            } → ${nowRunning ? "running" : "stopped"}`
          );
        }
      }
    } catch (error) {
      console.error("❌ Failed to fetch scraper status:", error);
      // Don't crash the app, just log the error
    }
  }, [isRunning]);

  // Use the useInterval hook to poll status every 5 seconds
  useInterval(fetchStatus, 5000);

  // Fetch initial status
  React.useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // WebSocket connection management
  useEffect(() => {
    if (isRunning && !socketRef.current) {
      // Connect to WebSocket through nginx proxy (same origin as frontend)
      // In production, this will be the Coolify domain with HTTPS
      // In development, this will be the frontend URL

      try {
        // Extract origin only — passing a path like /api to io() makes Socket.IO
        // treat it as a namespace, causing "Invalid namespace" errors.
        const apiUrl = ((import.meta as any).env.VITE_API_URL as string) || '';
        let socketUrl = window.location.origin;
        try { if (apiUrl) socketUrl = new URL(apiUrl).origin; } catch { /* use default */ }

        socketRef.current = io(socketUrl, {
          path: "/socket.io/",
          transports: ["polling", "websocket"], // Start with polling, upgrade to websocket
          upgrade: true,
          rememberUpgrade: true,
          // SSL-compatible settings for Coolify
          secure: window.location.protocol === "https:",
          rejectUnauthorized: false, // Allow self-signed certificates in development
          // Timeout settings for proxy stability
          timeout: 20000,
          forceNew: true,
        });

        socketRef.current.on("connect", () => {
          console.log("🔌 Connected to scraper WebSocket");
          setIsConnected(true);
          socketRef.current?.emit("join-scraper-room");
        });

        socketRef.current.on("disconnect", (reason) => {
          console.log("🔌 Disconnected from scraper WebSocket:", reason);
          setIsConnected(false);
        });

        socketRef.current.on(
          "page-update",
          (data: {
            url: string;
            html: string;
            contactId?: string;
            timestamp: string;
          }) => {
            try {
              console.log(
                "📄 Received page update:",
                data.url,
                "HTML length:",
                data.html?.length || 0
              );

              // Validate data before processing
              if (
                !data ||
                typeof data.url !== "string" ||
                typeof data.html !== "string"
              ) {
                console.warn("⚠️ Invalid page update data received:", data);
                return;
              }

              // If this is a new contact (different contactId), clear the previous contact data
              if (
                data.contactId &&
                currentContactIdRef.current &&
                data.contactId !== currentContactIdRef.current
              ) {
                console.log(
                  "🔄 New contact detected, clearing previous contact data"
                );
                setCurrentContactData(null);
              }

              // Update the current contact ID reference
              if (data.contactId) {
                currentContactIdRef.current = data.contactId;
              }

              setCurrentPage(data);
            } catch (error) {
              console.error("❌ Error processing page update:", error);
            }
          }
        );

        socketRef.current.on(
          "contact-completed",
          (data: {
            contactId: string;
            contactData: ContactData;
            timestamp: string;
          }) => {
            try {
              console.log("✅ Contact completed:", data.contactData);

              // Validate contact data
              if (!data || !data.contactData) {
                console.warn(
                  "⚠️ Invalid contact completion data received:",
                  data
                );
                return;
              }

              setCurrentContactData(data.contactData);
            } catch (error) {
              console.error("❌ Error processing contact completion:", error);
            }
          }
        );

        socketRef.current.on(
          "cleanup-contact-data",
          (data: { contactId: string; timestamp: string }) => {
            try {
              console.log("🧹 Cleaning up contact data:", data.contactId);
              // Keep the current page displayed until next contact starts
              // Only clear contact data, not the page
              // setCurrentPage(null); // Commented out to keep page visible
            } catch (error) {
              console.error("❌ Error processing cleanup:", error);
            }
          }
        );

        socketRef.current.on("connect_error", (error) => {
          console.error("❌ WebSocket connection error:", error);
          setIsConnected(false);
        });

        socketRef.current.on("error", (error) => {
          console.error("❌ WebSocket error:", error);
        });
      } catch (error) {
        console.error("❌ Error setting up WebSocket connection:", error);
        setIsConnected(false);
      }
    } else if (!isRunning && socketRef.current) {
      try {
        // Disconnect WebSocket when scraper stops
        console.log("🔌 Disconnecting WebSocket...");
        socketRef.current.emit("leave-scraper-room");
        socketRef.current.disconnect();
        socketRef.current = null;
        setIsConnected(false);
        setCurrentPage(null);
        setCurrentContactData(null);
        currentContactIdRef.current = null;
      } catch (error) {
        console.error("❌ Error disconnecting WebSocket:", error);
      }
    }

    // Cleanup on unmount
    return () => {
      try {
        if (socketRef.current) {
          console.log("🧹 Cleaning up WebSocket connection...");
          socketRef.current.emit("leave-scraper-room");
          socketRef.current.disconnect();
          socketRef.current = null;
        }
        setCurrentContactData(null);
        currentContactIdRef.current = null;
      } catch (error) {
        console.error("❌ Error during WebSocket cleanup:", error);
      }
    };
  }, [isRunning]);

  // Handle remote interactions
  const handleRemoteInteraction = (
    type: "click" | "type" | "scroll" | "refresh",
    data: any
  ) => {
    if (!socketRef.current || !isConnected) return;

    // Scale coordinates if needed
    // The scraper page is usually 1366x768 or 1280x720, but displayed smaller
    // We need to map clicks relative to the iframe size

    socketRef.current.emit("remote-interaction", { type, ...data });
  };

  // Update iframe content when page changes
  useEffect(() => {
    if (currentPage && iframeRef.current) {
      // We use srcDoc to safely render the HTML content without cross-origin issues
      // We also inject a script to capture clicks and keypresses for remote control
      const iframe = iframeRef.current;

      // Inject remote control script into the HTML
      const remoteControlScript = `
          <script>
            document.addEventListener('click', (e) => {
              // e.preventDefault(); // Optional: prevent default if we want strictly remote control
              const x = e.clientX;
              const y = e.clientY;
              const selector = e.target.id ? '#' + e.target.id : e.target.tagName;
              window.parent.postMessage({ type: 'remote-click', x, y, selector }, '*');
            }, true);
            
            document.addEventListener('keydown', (e) => {
              window.parent.postMessage({ type: 'remote-type', key: e.key }, '*');
            }, true);

            // Forward text input
             document.addEventListener('input', (e) => {
              if(e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                 window.parent.postMessage({ type: 'remote-type', text: e.target.value }, '*');
              }
            }, true);
          </script>
          <style>
            /* Highlight interactive elements */
            a, button, input { cursor: pointer; outline: 1px solid rgba(0,0,255,0.1); }
          </style>
        `;

      let finalHtml = currentPage.html;
      // Inject before body close or at end
      if (finalHtml.includes('</body>')) {
        finalHtml = finalHtml.replace('</body>', remoteControlScript + '</body>');
      } else {
        finalHtml += remoteControlScript;
      }

      // Use srcDoc to render content
      iframe.srcdoc = finalHtml;
    }
  }, [currentPage]);

  // Listen for messages from the iframe (remote control events)
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data && event.data.type) {
        if (event.data.type === 'remote-click') {
          handleRemoteInteraction('click', { x: event.data.x, y: event.data.y, selector: event.data.selector });
        } else if (event.data.type === 'remote-type') {
          if (event.data.text) {
            // Debounce typing if needed, but for now direct
            handleRemoteInteraction('type', { text: event.data.text });
          } else if (event.data.key) {
            handleRemoteInteraction('type', { key: event.data.key });
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isConnected]);

  const handleStartScraper = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      console.log("🚀 Starting scraper...");
      const response = await axios.post("/api/scraper/start");

      if (response.data.success) {
        setMessage({
          text: response.data.message || "Scraper avviato con successo",
          type: "success",
        });
        console.log("✅ Scraper started successfully");
        // Status will be updated by the polling
      } else {
        setMessage({
          text: response.data.message || "Impossibile avviare lo scraper",
          type: "error",
        });
        console.error("❌ Failed to start scraper:", response.data.message);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Errore di rete: Impossibile avviare lo scraper";
      setMessage({ text: errorMessage, type: "error" });
      console.error("❌ Error starting scraper:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopScraper = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      console.log("🛑 Stopping scraper...");
      const response = await axios.post("/api/scraper/stop");

      if (response.data.success) {
        setMessage({
          text: response.data.message || "Segnale di arresto inviato allo scraper",
          type: "info",
        });
        console.log("✅ Stop signal sent to scraper");
        // Status will be updated by the polling
      } else {
        setMessage({
          text: response.data.message || "Impossibile fermare lo scraper",
          type: "error",
        });
        console.error("❌ Failed to stop scraper:", response.data.message);
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message ||
        "Errore di rete: Impossibile fermare lo scraper";
      setMessage({ text: errorMessage, type: "error" });
      console.error("❌ Error stopping scraper:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkedInAuth = async () => {
    if (!cookieInput.trim()) {
      setMessage({
        text: "Per favore incolla il JSON dei cookie di LinkedIn",
        type: "error",
      });
      return;
    }

    setIsAuthenticating(true);
    setMessage(null);

    try {
      // Validate JSON format
      const cookies = JSON.parse(cookieInput);

      if (!Array.isArray(cookies)) {
        throw new Error("Cookies must be an array");
      }

      console.log("🔐 Authenticating with LinkedIn cookies...");
      const response = await axios.post("/api/scraper/authenticate", {
        cookies: cookies,
      });

      if (response.data.success) {
        setMessage({
          text: "Autenticazione LinkedIn riuscita! Ora puoi avviare lo scraper.",
          type: "success",
        });
        setShowCookieModal(false);
        setCookieInput("");
        console.log("✅ LinkedIn authentication successful");
      } else {
        setMessage({
          text: response.data.message || "Autenticazione fallita",
          type: "error",
        });
        console.error("❌ Authentication failed:", response.data.message);
      }
    } catch (error: any) {
      let errorMessage = "Formato JSON non valido";

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message && !error.message.includes("JSON")) {
        errorMessage = error.message;
      }

      setMessage({ text: errorMessage, type: "error" });
      console.error("❌ Error during authentication:", error);
    } finally {
      setIsAuthenticating(false);
    }
  };

  return (
    <Container>
      <Card>
        <Title>Controllo Scraper LinkedIn</Title>
        <Description>
          Gestisci lo scraper di contatti LinkedIn che aggiorna automaticamente le informazioni di contatto dai profili LinkedIn. 
          Lo scraper elabora i contatti che non sono stati aggiornati negli ultimi 30 giorni.
        </Description>

        <StatusSection>
          <StatusLabel>Stato Scraper</StatusLabel>
          <StatusIndicator $isRunning={isRunning}>
            <StatusDot $isRunning={isRunning} />
            {isRunning ? "In Esecuzione" : "Fermato"}
          </StatusIndicator>
        </StatusSection>

        <ButtonContainer>
          {isAdmin && (
            <>
              <Button
                onClick={() => setShowCookieModal(true)}
                $disabled={isLoading || isAuthenticating}
                $variant="primary"
              >
                {isAuthenticating ? <LoadingSpinner /> : <Icon>🔐</Icon>}
                Login LinkedIn
              </Button>

              <Button
                onClick={handleStartScraper}
                $disabled={isRunning || isLoading}
                $variant="primary"
              >
                {isLoading ? <LoadingSpinner /> : <Icon>▶️</Icon>}
                Avvia Scraper
              </Button>

              <Button
                onClick={handleStopScraper}
                $disabled={!isRunning || isLoading}
                $variant="secondary"
              >
                {isLoading ? <LoadingSpinner /> : <Icon>⏹️</Icon>}
                Ferma Scraper
              </Button>
            </>
          )}
        </ButtonContainer>

        {message && (
          <MessageContainer $type={message.type}>
            {message.text}
          </MessageContainer>
        )}
      </Card>

      {isRunning && (
        <Card>
          <ViewerSection>
            <ViewerTitle>
              🖥️ Vista Scraper Live
              {isConnected && (
                <span style={{ color: "#10b981", fontSize: "0.875rem" }}>
                  ● Connesso
                </span>
              )}
              {!isConnected && (
                <span style={{ color: "#ef4444", fontSize: "0.875rem" }}>
                  ● Disconnesso
                </span>
              )}
            </ViewerTitle>

            {/* Contact Data Card - Always Visible */}
            {currentContactData ? (
              <ContactDataCard>
                <ContactDataTitle>👤 Dati Contatto Estratti</ContactDataTitle>
                <ContactDataGrid>
                  <ContactDataItem>
                    <ContactDataLabel>Nome</ContactDataLabel>
                    <ContactDataValue>
                      {currentContactData.first_name}{" "}
                      {currentContactData.last_name}
                    </ContactDataValue>
                  </ContactDataItem>
                  {currentContactData.company && (
                    <ContactDataItem>
                      <ContactDataLabel>Azienda</ContactDataLabel>
                      <ContactDataValue>
                        {currentContactData.company}
                      </ContactDataValue>
                    </ContactDataItem>
                  )}
                  {currentContactData.role && (
                    <ContactDataItem>
                      <ContactDataLabel>Ruolo</ContactDataLabel>
                      <ContactDataValue>
                        {currentContactData.role}
                      </ContactDataValue>
                    </ContactDataItem>
                  )}
                </ContactDataGrid>
              </ContactDataCard>
            ) : (
              <LoadingCard>
                <LoadingAnimation />
                <LoadingText>
                  {currentPage
                    ? "Elaborazione dati contatto..."
                    : "In attesa del caricamento del contatto..."}
                </LoadingText>
              </LoadingCard>
            )}

            <ViewerContainer>
              <ViewerStatus>
                {currentPage
                  ? `Aggiornato: ${new Date(
                    currentPage.timestamp
                  ).toLocaleTimeString()}`
                  : "In attesa della pagina..."}
              </ViewerStatus>
              {currentPage ? (
                <ViewerIframe
                  ref={iframeRef}
                  title="Scraper Live View"
                  sandbox="allow-same-origin"
                />
              ) : (
                <ViewerPlaceholder>
                  <div style={{ fontSize: "3rem", marginBottom: "16px" }}>
                    🤖
                  </div>
                  <div>In attesa che lo scraper navighi su una pagina...</div>
                  <div
                    style={{
                      fontSize: "0.875rem",
                      color: "#9ca3af",
                      marginTop: "8px",
                    }}
                  >
                    La pagina apparirà qui quando lo scraper inizierà la navigazione
                  </div>
                </ViewerPlaceholder>
              )}
            </ViewerContainer>
          </ViewerSection>
        </Card>
      )}

      {/* LinkedIn Authentication Modal */}
      {showCookieModal && (
        <ModalOverlay onClick={() => setShowCookieModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              🔐 Autenticazione LinkedIn
            </ModalTitle>
            <ModalDescription>
              Per autenticarti con LinkedIn, devi estrarre i cookie di sessione.
              Usa l'estensione Chrome o segui le istruzioni manuali qui sotto.
            </ModalDescription>

            <InstructionsList>
              <li>Installa l'estensione Chrome "EditThisCookie" o simile</li>
              <li>Vai su <code>linkedin.com</code> e assicurati di aver effettuato l'accesso</li>
              <li>Clicca sull'icona dell'estensione ed esporta i cookie in JSON</li>
              <li>Incolla i cookie nell'area di testo qui sotto</li>
              <li>Clicca "Autentica" per iniettare i cookie nello scraper</li>
            </InstructionsList>

            <TextArea
              value={cookieInput}
              onChange={(e) => setCookieInput(e.target.value)}
              placeholder={`Incolla qui il JSON dei tuoi cookie LinkedIn, per esempio:
[
  {
    "name": "li_at",
    "value": "your-session-token",
    "domain": ".linkedin.com",
    "path": "/",
    "secure": true,
    "httpOnly": true
  },
  {
    "name": "JSESSIONID",
    "value": "your-session-id",
    "domain": ".linkedin.com",
    "path": "/",
    "secure": true
  }
]`}
            />

            <ModalButtonContainer>
              <ModalButton
                $variant="secondary"
                onClick={() => setShowCookieModal(false)}
                $disabled={isAuthenticating}
              >
                Annulla
              </ModalButton>
              <ModalButton
                onClick={handleLinkedInAuth}
                $disabled={isAuthenticating || !cookieInput.trim()}
              >
                {isAuthenticating ? <LoadingSpinner /> : "🔐"}
                Autentica
              </ModalButton>
            </ModalButtonContainer>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default ScraperControl;
